import React, { useState } from 'react';
import { Box, Tabs, Tab, Button, Grid, Divider, Typography } from '@mui/material';

// Import our custom components
import SurveySection from './SurveySection';
import SurveySummary from './SurveySummary';

// Import constants
import { UI_CONFIG, TAB_NAVIGATION } from '../utils/constants';

/**
 * SurveyTabs Component
 * 
 * Manages the tabbed interface for the survey, including:
 * - Tab navigation between survey sections
 * - Summary tab for strengths/opportunities
 * - Navigation buttons (Back/Next/Submit)
 * - Tab change handling with auto-save and validation
 * 
 * @param {Object} props - Component props
 * @param {Object} props.control - React Hook Form control object
 * @param {Object} props.errors - Form validation errors
 * @param {Object} props.surveyInfo - Survey structure data
 * @param {Function} props.onTabChange - Handler for tab changes
 * @param {boolean} props.isLoading - Whether the form is currently saving
 * @param {number} props.value - Externally controlled active tab index (optional)
 */
const SurveyTabs = ({
  control,
  errors,
  surveyInfo,
  onTabChange,
  isLoading = false,
  value,
}) => {
  // Current active tab index (0-based) - use external value if provided, otherwise internal state
  const [internalActiveTab, setInternalActiveTab] = useState(0);
  const activeTab = value !== undefined ? value : internalActiveTab;

  // Get section keys for tab generation
  const sectionKeys = Object.keys(surveyInfo);
  const totalSections = sectionKeys.length;
  const summaryTabIndex = totalSections; // Summary is the last tab

  /**
   * Handles tab change events
   * Calls the parent's onTabChange handler for validation and auto-save
   */
  const handleTabChange = async (event, newTabIndex) => {
    if (isLoading) {
      // Prevent tab changes while saving
      return;
    }

    try {
      // Call parent handler for validation and auto-save
      if (onTabChange) {
        await onTabChange(event, newTabIndex);
      }
      
      // Only update internal state if not externally controlled
      if (value === undefined) {
        setInternalActiveTab(newTabIndex);
      }
    } catch (error) {
      // Tab change failed, stay on current tab
    }
  };

  /**
   * Handles navigation button clicks (Back/Next)
   * This triggers a save via handleTabChange when navigating
   */
  const handleNavigation = async (event, direction) => {
    const newTabIndex = direction === 'next' ? activeTab + 1 : activeTab - 1;
    
    // Validate bounds
    if (newTabIndex < 0 || newTabIndex > summaryTabIndex) {
      return;
    }

    // This will trigger a save via the parent's onTabChange handler
    await handleTabChange(event, newTabIndex);
  };

  /**
   * Custom TabPanel component for rendering tab content
   * Matches the original styling with background color and padding
   */
  const TabPanel = ({ children, value, index, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`survey-tabpanel-${index}`}
        aria-labelledby={`survey-tab-${index}`}
        style={{ padding: 8, backgroundColor: 'rgba(195,202,216,.3)' }}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3, padding: 0 }}>
            <Typography style={{ fontFamily: 'Lato' }}>{children}</Typography>
          </Box>
        )}
      </div>
    );
  };

  /**
   * Accessibility props for tabs
   */
  const getTabProps = (index) => ({
    id: `survey-tab-${index}`,
    'aria-controls': `survey-tabpanel-${index}`,
  });

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tab Headers - Fixed */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="survey sections"
          variant={UI_CONFIG.TABS_VARIANT}
          scrollButtons={UI_CONFIG.TABS_SCROLL_BUTTONS}
          disabled={isLoading}
          sx={{
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }}
        >
          {/* Section Tabs */}
          {sectionKeys.map((sectionKey, index) => (
            <Tab 
              key={sectionKey}
              label={sectionKey} 
              {...getTabProps(index)}
              disabled={isLoading}
            />
          ))}
          
          {/* Summary Tab */}
          <Tab 
            label={TAB_NAVIGATION.SUMMARY_TAB_LABEL}
            {...getTabProps(summaryTabIndex)}
            disabled={isLoading}
          />
        </Tabs>
      </Box>

      {/* Scrollable Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {/* Section Content */}
        {sectionKeys.map((sectionKey, sectionIndex) => (
          <TabPanel 
            key={sectionKey}
            value={activeTab} 
            index={sectionIndex}
          >
            <SurveySection
              control={control}
              errors={errors}
              sectionInfo={surveyInfo[sectionKey]}
              sectionKey={sectionKey}
              sectionIndex={sectionIndex}
            />
          </TabPanel>
        ))}

        {/* Summary Tab Content */}
        <TabPanel 
          value={activeTab} 
          index={summaryTabIndex}
        >
          <SurveySummary
            control={control}
            errors={errors}
          />
        </TabPanel>
      </Box>

      {/* Fixed Navigation Buttons */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Divider style={{ margin: UI_CONFIG.DIVIDER_MARGIN }} />
        <Grid container justifyContent="space-between" spacing={2}>
          {/* Back Button */}
          <Grid item>
            <Button
              onClick={(e) => handleNavigation(e, 'back')}
              size="large"
              variant="contained"
              color="inherit"
              style={{ marginLeft: 16 }}
              disabled={activeTab === 0 || isLoading}
            >
              {TAB_NAVIGATION.BACK_BUTTON_LABEL}
            </Button>
          </Grid>

          {/* Next/Submit Button */}
          <Grid item>
            {activeTab === summaryTabIndex ? (
              // Submit Button (on summary tab)
              <Button
                size="large"
                variant="contained"
                color="primary"
                type="submit"
                style={{ marginRight: 16 }}
                disabled={isLoading}
              >
                {isLoading ? 'Submitting...' : TAB_NAVIGATION.SUBMIT_BUTTON_LABEL}
              </Button>
            ) : (
              // Next Button (on section tabs)
              <Button
                onClick={(e) => handleNavigation(e, 'next')}
                size="large"
                variant="contained"
                color="primary"
                style={{ marginRight: 16 }}
                disabled={isLoading}
              >
                {TAB_NAVIGATION.NEXT_BUTTON_LABEL}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Loading Indicator */}
      {isLoading && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Please wait while we save your progress...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SurveyTabs;